import SelectInputClient from "@components/common/SelectInputClient";
import DeleteIcon from "@components/icons/DeleteIcon";
import FloppyDiskIcon from "@components/icons/FloppyDiskIcon";
import ModalDelete from "@components/Modals/ModalDelete";
import { useIsMobile } from "@components/mediaQuery/useIsMobile";
import { setPeplinkGroup, setPeplinkOraganization } from "@store/modules/vesselsSlice";
import { IS_ADMIN_NAME, IS_ADMIN_ROLE, IS_SALES_STAFF_NAME } from "@utils/user_roles";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
	fetchAllSubscriptions,
	fetchGroupeByOrganization,
	fetchOrganizationList,
	setCreatePeplinkConfiguration,
	setDeletePeplinkConfiguration,
	setUpdatePeplinkConfiguration,
} from "../../../../services/vesselService";
import Button from "../../../common/Button";
import Input from "../../../common/Input";
import SelectInput from "../../../common/SelectInput";
import SelectMultipleInputs from "../../../common/SelectMultipleInputs";
import ModalAlert from "../../../Modals/ModalAlert";

const useFetchGroup = (
	searchTermGroup,
	token,
	organizationId,
	groupeId,
	dispatch,
	pageGroup,
	navigate,
	setLoaderGroup,
	setHasMoreGroup,
	filtredGroupe,
) => {
	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;

		const fetchGroupData = debounce(() => {
			if (token && organizationId && pageGroup > 0) {
				setLoaderGroup(true);
				fetchGroupeByOrganization(
					token,
					organizationId,
					groupeId,
					dispatch,
					navigate,
					pageGroup,
					10,
					1, // paginate
					{ groupName: searchTermGroup },
					signal,
				)
					.then((data) => {
						const totalPage = Math.ceil(data.total / data.perPage);
						const updatedOptions = pageGroup === 1 ? data.data : [...filtredGroupe, ...data.data];
						dispatch(setPeplinkGroup(updatedOptions || []));
						setHasMoreGroup(totalPage > pageGroup);
					})
					.catch((error) => console.error("Failed to fetch groups:", error))
					.finally(() => setLoaderGroup(false));
			}
		}, 300);

		fetchGroupData();

		return () => {
			controller.abort();
			fetchGroupData.cancel();
		};
	}, [
		searchTermGroup,
		token,
		dispatch,
		navigate,
		organizationId,
		groupeId,
		setHasMoreGroup,
		pageGroup,
		setLoaderGroup,
	]);
};

const PeplinkConfiguration = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [isFormValid, setIsFormValid] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [showSuccessModal, setShowSuccessModal] = useState(false);

	const [isUpdate, setIsUpdate] = useState(false);

	const [serialNumber, setSerialNumber] = useState("");
	const [peplinkId, setPeplinkId] = useState("");
	const [organizationId, setOrganizationId] = useState("");
	const [groupeId, setGroupeId] = useState("");
	const [hasPeplinkSubscription, setHasPeplinkSubscription] = useState("no");
	const [peplinkSubscriptionPlan, setPeplinkSubscriptionPlan] = useState([]);
	const [errors, setErrors] = useState({});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [loadingOrganization, setLoadingOrganization] = useState(false);
	const [loadingGroupe, setLoadingGroupe] = useState(false);

	const [pageGroup, setPageGroup] = useState(1);
	const [loaderGroup, setLoaderGroup] = useState(false);
	const [hasMoreGroup, setHasMoreGroup] = useState(true);
	const [searchTermGroup, setSearchTermGroup] = useState("");

	const debouncedFetchGroupeRef = useRef(null);

	const deletion_message = "";

	const { id } = useParams();
	const vesselDetails = useSelector((state) => state.modules.vessels.vesselDetails);
	const token = useSelector((state) => state.auth.user.token);
	const peplinkPlans = useSelector((state) => state.modules.vessels?.peplink?.subscription);

	const organizationList = useSelector((state) => state.modules.vessels?.peplink?.oraganization);

	const filtredGroupe = useSelector((state) => state.modules.vessels?.peplink?.group);

	const userrole = useSelector((state) => state.auth.user.role?.name);
	const userparentrole = useSelector((state) => state.auth.user.role?.parent_id);
	useEffect(() => {
		setIsFormValid(serialNumber);
	}, [serialNumber, errors]);

	useEffect(() => {
		let peplinkBoxFound = false;

		vesselDetails?.smartboxes?.forEach(async (box) => {
			if (box?.smartbox?.name === "Peplink") {
				peplinkBoxFound = true;
				setIsUpdate(true);
				setSerialNumber(box?.serial_number || "");
				setPeplinkId(box?.id);

				if (box?.subscriptions?.length > 0) {
					setHasPeplinkSubscription("yes");
					const subscriptionIds = box.subscriptions.map((sub) => sub.id);
					setPeplinkSubscriptionPlan(subscriptionIds);
				}

				const device = vesselDetails?.peplink_device;
				const orgId = device?.organization_id || "";
				setOrganizationId(orgId);

				if (orgId && debouncedFetchGroupeRef.current) {
					debouncedFetchGroupeRef.current(orgId, device?.group_id);
				}

				setGroupeId(device?.group_id);
				setSearchTermGroup(device?.group_name);
			}
		});

		if (!peplinkBoxFound) {
			setIsUpdate(false);
			setSerialNumber("");
			setHasPeplinkSubscription("no");
			setPeplinkSubscriptionPlan([]);
		}
	}, [vesselDetails]);

	useEffect(() => {
		const debouncedFetch = debounce(() => {
			fetchAllSubscriptions(token, dispatch, navigate);
		}, 300);

		const debouncedOrganizationFetch = debounce(() => {
			fetchOragnizations(token, dispatch, navigate);
		}, 300);

		debouncedFetch();
		if (isUpdate) {
			if (
				userrole === IS_ADMIN_NAME ||
				userparentrole === IS_ADMIN_ROLE ||
				userrole === IS_SALES_STAFF_NAME
			) {
				debouncedOrganizationFetch();
			}
		}

		return () => {
			debouncedFetch.cancel();
			debouncedOrganizationFetch.cancel();
		};
	}, [dispatch, token, navigate, isUpdate]);

	const handlepeplinkSubscriptionChange = (id, selectedIds) => {
		if (selectedIds.length > 2) {
			setErrors({
				peplinkSubscriptionPlan: "You can select a maximum of two plans.",
			});
		} else {
			setPeplinkSubscriptionPlan(selectedIds);
			setErrors({ peplinkSubscriptionPlan: "" });
		}
	};
	(userrole === IS_ADMIN_NAME ||
		userparentrole === IS_ADMIN_ROLE ||
		userrole === IS_SALES_STAFF_NAME) &&
		useFetchGroup(
			searchTermGroup,
			token,
			organizationId,
			groupeId,
			dispatch,
			pageGroup,
			navigate,
			setLoaderGroup,
			setHasMoreGroup,
			filtredGroupe,
		);

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (!isFormValid) return;

		setLoading(true);
		setErrors({});

		const data = {
			serial_number: serialNumber,
		};
		if (organizationId) {
			data.organization_id = organizationId;
		}

		if (groupeId) {
			data.group_id = groupeId;
		}

		if (hasPeplinkSubscription === "yes") {
			if (peplinkSubscriptionPlan.length < 1 || peplinkSubscriptionPlan.length > 2) {
				setErrors((prevErrors) => ({
					...prevErrors,
					peplinkSubscriptionPlan: "You must select at least one to two plans.",
				}));

				return setLoading(false);
			} else {
				data.subscriptions = peplinkSubscriptionPlan;
			}
		}

		try {
			const apiCall = isUpdate ? setUpdatePeplinkConfiguration : setCreatePeplinkConfiguration;

			const response = await apiCall(data, id, token, dispatch, navigate);

			if (response.status === 200 || response.status === 201 || response.status === 204) {
				setShowSuccessModal(true);
				setTimeout(() => handleRedirectionVessel(), 1500);
			} else {
				if (response.data?.errors) {
					setErrors(response.data.errors);
				} else {
					setShowErrorModal(true);
				}
			}
		} catch (error) {
			console.error("Error in API call:", error);
			setShowErrorModal(true);
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (id) => {
		setLoading(true);
		setErrors({});

		try {
			const response = await setDeletePeplinkConfiguration(id, token, dispatch, navigate);

			if ([200, 201, 202, 204].includes(response.status)) {
				setShowSuccessModal(true);

				setTimeout(() => {
					handleRedirectionVessel();
				}, 2000);
				return true;
			} else {
				if (response.data?.errors) {
					setErrors(response.data.errors);
				} else {
					setShowErrorModal(true);
				}
				return false;
			}
		} catch (error) {
			console.error("Error in delete peplink :", error);
			setShowErrorModal(true);
			return false;
		} finally {
			setLoading(false);
		}
	};

	const handleRedirectionVessel = () => navigate("/vessels_site");

	const openModal = (id) => {
		setPeplinkId(id);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	const fetchOragnizations = async (type) => {
		setLoadingOrganization(true);

		try {
			const datalist = await fetchOrganizationList(token, dispatch, navigate);
			dispatch(setPeplinkOraganization(datalist.data));
			setLoadingOrganization(false);
		} catch (error) {
			console.error("Failed to fetch organization:", error);
		} finally {
			setLoadingOrganization(false);
		}
	};
	const handleOrganizationChange = async (id, value, name) => {
		if (value) {
			setOrganizationId(value);
			setGroupeId(null);
			setSearchTermGroup(null);
		} else {
			setOrganizationId("");
			setGroupeId("");
		}
	};

	const handleSearchSelectChange = useCallback(
		(id, value) => {
			setSearchTermGroup(value);
			setPageGroup(1);
		},
		[searchTermGroup],
	);

	const handleGroupChange = (id, value, name) => {
		if (value) {
			setSearchTermGroup(name);
			setGroupeId(value);
			setPageGroup(1);
		} else {
			setGroupeId(null);
			setSearchTermGroup(null);
		}
	};
	const isMobile = useIsMobile();
	return (
		<div className="px-4 pb-4">
			<div className="w-full rounded-lg border border-navbar-bg bg-navbar-bg p-6 shadow">
				<form className="grid gap-6" onSubmit={handleSubmit}>
					<span className="font-semibold text-base text-white sm:text-lg md:text-xl lg:text-2xl">
						Peplink Configuration
					</span>

					<Input
						id="serialNumber"
						placeholder="Serial Number"
						label="Serial Number *"
						value={serialNumber || ""}
						className={`block h-9 w-9 rounded-lg border border-gray-600 py-3 text-gray-400 text-sm placeholder-gray-400 ${
							errors.serial_number ? "border-red-500" : ""
						}`}
						required
						onChange={(value) => {
							setSerialNumber(value);
						}}
						onClear={() => setSerialNumber(null)}
					/>

					{errors.serial_number && <span className="text-red-500">{errors.serial_number[0]}</span>}

					<SelectInput
						id="peplinkSubscription"
						dataTestId="peplinkSubscription-select"
						label="Has LTE Subscription? *"
						placeholder="No"
						value={hasPeplinkSubscription}
						onChange={(id, value) => setHasPeplinkSubscription(value)}
						options={[
							{ id: "yes", name: "Yes", value: "yes" },
							{ id: "no", name: "No", value: "no" },
						]}
						marginTop={`${isMobile ? "mt-2" : "mt-20"}`}
						classDropdown={`${isMobile ? "relative  " : "absolute z-50"}`}
					/>

					{hasPeplinkSubscription === "yes" && (
						<SelectMultipleInputs
							id="peplinkSubscriptionPlan"
							label="LTE Subscription Plan *"
							placeholder="Select up to two plans"
							value={peplinkSubscriptionPlan}
							onChange={handlepeplinkSubscriptionChange}
							options={peplinkPlans}
							displayField="name"
							className={`block w-full rounded-lg border bg-dark-blue py-3 text-gray-400 text-sm placeholder-gray-400 ${
								errors.peplinkSubscriptionPlan ? "border-red-500" : ""
							}`}
							error={errors.peplinkSubscriptionPlan}
							marginTop={`${isMobile ? "mt-2" : "mt-20"}`}
							classDropdown={`${isMobile ? "relative  " : "absolute z-50"}`}
						/>
					)}

					{errors.peplinkSubscriptionPlan && (
						<span className="text-red-500">{errors.peplinkSubscriptionPlan}</span>
					)}
					{/*  select Organization */}

					{isUpdate &&
						(userrole === IS_ADMIN_NAME ||
							userparentrole === IS_ADMIN_ROLE ||
							userrole === IS_SALES_STAFF_NAME) && (
							<>
								{loadingOrganization ? (
									<div className="flex items-center space-x-2">
										<svg
											className="h-5 w-5 animate-spin text-blue-gray"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
										>
											<circle
												className="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												strokeWidth="4"
											></circle>
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 11-8 8z"
											></path>
										</svg>
										<span className="text-blue-gray text-sm">Loading Organization...</span>
									</div>
								) : (
									<>
										<SelectInput
											id="peplinkOrganization"
											dataTestId="peplinkOrganization-select"
											label="Organization"
											placeholder="Enter organization"
											value={organizationId}
											onChange={handleOrganizationChange}
											options={organizationList}
											displayField="organization_name"
											marginTop={`${isMobile ? "mt-2" : "mt-20"}`}
											classDropdown={`${isMobile ? "relative  " : "absolute z-50"}`}
										/>
										{errors?.organization && (
											<p className="text-red-500 text-sm">{errors?.organization}</p>
										)}
									</>
								)}

								{loadingGroupe ? (
									<div className="flex items-center space-x-2">
										<svg
											className="h-5 w-5 animate-spin text-blue-gray"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
										>
											<circle
												className="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												strokeWidth="4"
											></circle>
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 11-8 8z"
											></path>
										</svg>
										<span className="text-blue-gray text-sm">Loading Group...</span>
									</div>
								) : (
									<>
										<SelectInputClient
											id="group"
											label={`Group : ${
												organizationId === "" ? "Select 'Organization' first" : ""
											}`}
											placeholder="Select Group"
											value={groupeId}
											onChange={handleGroupChange}
											search={handleSearchSelectChange}
											options={filtredGroupe?.map((group) => ({
												id: group?.group_id,
												group_name: group?.group_name,
											}))}
											hasMore={hasMoreGroup}
											loader={loaderGroup}
											displayField="group_name"
											disabled={organizationId === "" || organizationId === null}
											fetchMoreData={() => setPageGroup((prev) => prev + 1)}
											marginTop={`${isMobile ? "mt-2" : "mt-20"}`}
											classDropdown={`${isMobile ? "relative  " : "absolute z-50"}`}
										/>

										{errors?.group && <p className="text-red-500 text-sm">{errors?.group}</p>}
									</>
								)}
							</>
						)}
					<div>
						<div className="flex justify-between sm:flex-row sm:space-x-6 sm:space-y-0 lg:space-x-8">
							<Button
								text={loading ? "Loading..." : isUpdate ? "Update" : "Create"}
								className={`text-white ${!isFormValid ? "opacity-50" : ""}`}
								disabled={!isFormValid || loading}
								onClick={handleSubmit}
								icon={FloppyDiskIcon}
							/>

							{isUpdate && (
								<Button
									text={loading ? "Loading..." : "Delete"}
									className={`text-white ${!isFormValid ? "opacity-50" : ""}`}
									disabled={loading}
									onClick={() => openModal(peplinkId)}
									icon={DeleteIcon}
								/>
							)}
						</div>
					</div>
				</form>
			</div>

			<ModalAlert
				isOpen={showErrorModal}
				type="error"
				closeModal={() => setShowErrorModal(false)}
			/>
			<ModalAlert
				isOpen={showSuccessModal}
				type="success"
				closeModal={() => setShowSuccessModal(false)}
				message={
					isUpdate
						? "Peplink configuration has been successfully updated!"
						: "Peplink configuration has been successfully added!"
				}
			/>
			<ModalDelete
				id={peplinkId}
				isOpen={isModalOpen}
				closeModal={closeModal}
				handleDelete={() => handleDelete(peplinkId)}
				response_message={deletion_message}
				response_error_message={deletion_message}
				siteName={serialNumber}
			/>
		</div>
	);
};

export default PeplinkConfiguration;
